import { useState, useEffect, useCallback } from 'react';
import { FileCheck, CheckCircle, XCircle, Loader2, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getCountryByCode } from '../../utils/countries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDocuments() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('in_attesa');
  const [previewUrl, setPreviewUrl] = useState(null);

  const token = localStorage.getItem('lifelink_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'tutti' ? {} : { status: filter };
      const { data } = await axios.get(`${API}/admin/documents`, { headers, params });
      setUsers(data.users || []);
    } catch (e) {
      toast.error('Errore caricamento documenti');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const reviewDoc = async (email, azione) => {
    const confirm = window.confirm(azione === 'approva'
      ? `Approvare il documento di ${email}?`
      : `Rifiutare il documento di ${email}? L'utente dovrà caricarlo di nuovo.`);
    if (!confirm) return;
    try {
      await axios.post(`${API}/admin/documents/review`, { email, azione }, { headers });
      toast.success(azione === 'approva' ? 'Documento approvato' : 'Documento rifiutato');
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const openDocPreview = async (fileId) => {
    if (!fileId) return;
    try {
      const resp = await axios.get(`${API}/files/${fileId}/download`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      setPreviewUrl(url);
    } catch (e) {
      toast.error('Impossibile caricare il documento');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-documents">
      <div className="flex items-center gap-3">
        <FileCheck size={16} className="text-[#FF3B30]" />
        <span className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">Verifica Documenti d'Identità</span>
        <span className="text-xs text-[#4A6B8A] uppercase tracking-widest">{users.length} utenti</span>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-white border border-[#B8D4E8] text-[#0D1B2A] p-2 text-xs uppercase tracking-widest focus:border-[#FF3B30] focus:outline-none"
          data-testid="docs-filter"
        >
          <option value="in_attesa">In Attesa</option>
          <option value="verificato">Verificati</option>
          <option value="tutti">Tutti</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-8"><Loader2 size={24} className="animate-spin inline text-[#4A6B8A]" /></div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 border border-[#B8D4E8] bg-white">
          <FileCheck size={48} className="mx-auto text-[#B8D4E8] mb-3" />
          <p className="text-sm text-[#4A6B8A]">Nessun documento da verificare</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {users.map((u) => (
            <div key={u.email} className="border border-[#B8D4E8] bg-white p-4" data-testid={`doc-card-${u.email}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-sm font-bold text-[#0D1B2A]">{u.nome} {u.cognome}</div>
                  <div className="text-xs text-[#4A6B8A]">{u.email}</div>
                  <div className="text-[10px] font-mono text-[#6B8AB5] mt-1">
                    {u.numero_passaporto
                      ? (() => {
                          const c = getCountryByCode(u.paese_passaporto);
                          return <span>{c ? c.flag : '📔'} Passaporto {c ? c.name : ''}: {u.numero_passaporto}</span>;
                        })()
                      : u.codice_iss
                        ? <span>🇸🇲 ISS: {u.codice_iss}</span>
                        : (u.codice_fiscale || '-')}
                  </div>
                </div>
                {u.documento_verificato ? (
                  <span className="text-[9px] uppercase tracking-widest bg-[#32D74B]/10 text-[#32D74B] px-2 py-1 border border-[#32D74B]/30 font-bold">
                    Verificato
                  </span>
                ) : (
                  <span className="text-[9px] uppercase tracking-widest bg-[#FF9F0A]/10 text-[#FF9F0A] px-2 py-1 border border-[#FF9F0A]/30 font-bold">
                    In Attesa
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  onClick={() => openDocPreview(u.documento_fronte_id)}
                  disabled={!u.documento_fronte_id}
                  className="flex items-center justify-center gap-1 py-2 border border-[#B8D4E8] text-[11px] uppercase tracking-widest text-[#4A6B8A] hover:border-[#0A84FF] hover:text-[#0A84FF] disabled:opacity-30"
                  data-testid={`doc-front-${u.email}`}
                >
                  <Eye size={12} /> Fronte
                </button>
                <button
                  onClick={() => openDocPreview(u.documento_retro_id)}
                  disabled={!u.documento_retro_id}
                  className="flex items-center justify-center gap-1 py-2 border border-[#B8D4E8] text-[11px] uppercase tracking-widest text-[#4A6B8A] hover:border-[#0A84FF] hover:text-[#0A84FF] disabled:opacity-30"
                  data-testid={`doc-back-${u.email}`}
                >
                  <Eye size={12} /> Retro
                </button>
              </div>
              {!u.documento_verificato && (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => reviewDoc(u.email, 'approva')}
                    className="flex items-center justify-center gap-1 py-2 bg-[#32D74B] text-white text-[11px] font-bold uppercase tracking-widest hover:bg-[#28B840]"
                    data-testid={`approve-doc-${u.email}`}
                  >
                    <CheckCircle size={12} /> Approva
                  </button>
                  <button
                    onClick={() => reviewDoc(u.email, 'rifiuta')}
                    className="flex items-center justify-center gap-1 py-2 bg-[#FF3B30] text-white text-[11px] font-bold uppercase tracking-widest hover:bg-[#FF1A1A]"
                    data-testid={`reject-doc-${u.email}`}
                  >
                    <XCircle size={12} /> Rifiuta
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {previewUrl && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }} data-testid="doc-preview-modal">
          <div className="relative max-w-4xl max-h-[90vh]">
            <img src={previewUrl} alt="Documento" className="max-w-full max-h-[90vh] object-contain" />
            <button
              onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
              className="absolute top-2 right-2 p-2 bg-white/90 hover:bg-white"
              data-testid="close-preview-btn"
            >
              <XCircle size={20} className="text-[#FF3B30]" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
